/**
 * Travellers owed money after a cancellation.
 *
 * A cancellation writes the debt here and moves nothing on its own: an admin
 * approves each refund, and Paystack reverses the original charge so the money
 * goes back to the card that paid. Where Paystack cannot do it — a charge too
 * old to reverse, one it no longer recognises — the refund stays in this queue
 * marked failed, because the traveller is still owed it; sending it by hand and
 * recording that here is the way out.
 */
import { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  RefreshCw,
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import {
  useRefundsQuery,
  useApproveRefundMutation,
  useMarkRefundPaidMutation,
  useDeclineRefundMutation,
  useRevertRefundMutation,
} from "../../api/query/useAdminQueries";
import { getApiErrorMessage } from "../../utils/apiError";

const PAGE_SIZE = 20;

const STATUS_FILTERS = [
  { value: "pending", label: "To refund" },
  { value: "processing", label: "With Paystack" },
  { value: "success", label: "Refunded" },
  { value: "failed", label: "Needs attention" },
  { value: "", label: "All" },
];

const STATUS_STYLES = {
  success: "bg-[#E4F5E4] text-[#008000]",
  processing: "bg-[#E8F0FE] text-[#1A56DB]",
  pending: "bg-[#FFF4DB] text-[#F99C07]",
  failed: "bg-[#FDE8E8] text-[#C81E1E]",
};

const STATUS_LABELS = {
  success: "Refunded",
  processing: "With Paystack",
  pending: "Awaiting approval",
  failed: "Needs attention",
};

const formatNaira = (amount) =>
  `₦${Number(amount || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const travellerName = (user) => {
  if (!user || typeof user !== "object") return "Unknown traveller";
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.email || "Unknown traveller";
};

function AdminRefunds() {
  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(1);
  const [menu, setMenu] = useState(null);
  // { refund, mode: "mark-paid" | "decline" | "revert" }
  const [prompt, setPrompt] = useState(null);
  const [promptValue, setPromptValue] = useState("");
  const [actionError, setActionError] = useState("");

  const approve = useApproveRefundMutation();
  const markPaid = useMarkRefundPaidMutation();
  const decline = useDeclineRefundMutation();
  const revert = useRevertRefundMutation();
  const isActing =
    approve.isPending || markPaid.isPending || decline.isPending || revert.isPending;

  const { data, isLoading, isError, error, refetch, isFetching } = useRefundsQuery({
    ...(status ? { status } : {}),
    page,
    limit: PAGE_SIZE,
  });

  const payload = data?.data || data || {};
  const refunds = Array.isArray(payload.items) ? payload.items : [];
  const pagination = payload.pagination || {};
  const totalPages = pagination.pages || 1;
  const total = pagination.total || 0;

  // The menu is pinned to a button inside a table that scrolls, so anything
  // that moves the table closes it rather than leaving it floating.
  useEffect(() => {
    if (!menu) return undefined;

    const close = () => setMenu(null);
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };

    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menu]);

  const changeStatus = (value) => {
    setStatus(value);
    setPage(1);
  };

  const toggleMenu = (event, refund) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenu((current) =>
      current?.id === refund.reference
        ? null
        : {
            id: refund.reference,
            refund,
            top: rect.bottom + 8,
            right: Math.max(16, window.innerWidth - rect.right),
          }
    );
  };

  const openPrompt = (refund, mode) => {
    setMenu(null);
    setPrompt({ refund, mode });
    setPromptValue("");
    setActionError("");
  };

  const closePrompt = () => {
    setPrompt(null);
    setPromptValue("");
    setActionError("");
  };

  const runAction = async (fn) => {
    setActionError("");
    try {
      await fn();
      closePrompt();
    } catch (err) {
      setActionError(getApiErrorMessage(err, "That did not go through. Try again."));
    }
  };

  const handleApprove = async (refund) => {
    setMenu(null);
    setActionError("");
    try {
      await approve.mutateAsync(refund.reference);
    } catch (err) {
      // Paystack's reason matters here — it is what tells an admin to send the
      // money by hand instead.
      window.alert(getApiErrorMessage(err, "Paystack could not send this refund."));
    }
  };

  const submitPrompt = () => {
    if (!prompt) return;
    const id = prompt.refund.reference;

    if (prompt.mode === "mark-paid") {
      return runAction(() => markPaid.mutateAsync({ refundId: id, note: promptValue.trim() }));
    }

    if (!promptValue.trim()) {
      setActionError(
        prompt.mode === "decline"
          ? "Tell the traveller why their refund was refused."
          : "Say what was wrong, so the record explains itself later."
      );
      return;
    }

    const reason = promptValue.trim();
    return runAction(() =>
      prompt.mode === "decline"
        ? decline.mutateAsync({ refundId: id, reason })
        : revert.mutateAsync({ refundId: id, reason })
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Refunds</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
            Money owed to travellers who cancelled. Approving reverses the
            original charge through Paystack, so it goes back to the card they
            paid with. Pulo's 20% service fee is never part of a refund.
          </p>
        </div>

        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-200 px-6 py-5 md:flex-row md:items-center md:justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {total} refund{total === 1 ? "" : "s"}
            </h2>

            <div className="flex flex-wrap items-center gap-2">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.value || "all"}
                  type="button"
                  onClick={() => changeStatus(filter.value)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                    status === filter.value
                      ? "border-[#570033] bg-[#570033] text-white"
                      : "border-[#C9D3E2] bg-white text-gray-700 hover:border-[#570033]"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isFetching}
                className="flex items-center gap-2 rounded-full border border-[#C9D3E2] bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-[#570033] disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  {["Traveller", "Amount", "Why", "Status", "Cancelled", "Reference", ""].map(
                    (heading, index) => (
                      <th
                        key={heading || `actions-${index}`}
                        className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500"
                      >
                        {heading}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      Loading refunds...
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-red-600">
                      {error?.message || "Unable to load refunds."}
                    </td>
                  </tr>
                ) : refunds.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      Nobody is waiting on a refund.
                    </td>
                  </tr>
                ) : (
                  refunds.map((refund) => (
                    <tr
                      key={refund._id || refund.reference}
                      className="border-b border-gray-100 last:border-0"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {travellerName(refund.user)}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {formatNaira(refund.amount)}
                      </td>
                      <td className="px-6 py-4 max-w-[220px] text-sm text-gray-600">
                        {refund.reason || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            STATUS_STYLES[refund.status] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {STATUS_LABELS[refund.status] || refund.status}
                        </span>
                        {refund.failureReason ? (
                          <span className="mt-1 block max-w-[260px] text-xs leading-4 text-gray-500">
                            {refund.failureReason}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(refund.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-xs font-medium text-gray-500">
                        <span className="block">{refund.reference}</span>
                        {refund.paymentReference ? (
                          <span className="mt-1 block text-[11px] text-gray-400">
                            Charge: {refund.paymentReference}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          aria-label={`Actions for ${refund.reference}`}
                          aria-haspopup="menu"
                          aria-expanded={menu?.id === refund.reference}
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => toggleMenu(event, refund)}
                          disabled={isActing}
                          className={`rounded-full border p-2 transition disabled:opacity-50 ${
                            menu?.id === refund.reference
                              ? "border-[#570033] bg-[#F8EEF4] text-[#570033]"
                              : "border-transparent text-gray-500 hover:border-[#C9D3E2] hover:text-gray-700"
                          }`}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
              <span className="text-sm text-gray-500">
                Page {pagination.page || page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={(pagination.page || page) <= 1}
                  className="flex items-center gap-1 rounded-full border border-[#C9D3E2] px-3 py-2 text-sm text-gray-700 transition hover:border-[#570033] disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={(pagination.page || page) >= totalPages}
                  className="flex items-center gap-1 rounded-full border border-[#C9D3E2] px-3 py-2 text-sm text-gray-700 transition hover:border-[#570033] disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : null}
        </section>
      </div>

      {menu ? (
        <div
          role="menu"
          onMouseDown={(event) => event.stopPropagation()}
          style={{ top: menu.top, right: menu.right }}
          className="fixed z-50 w-56 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 shadow-lg"
        >
          {menu.refund.status === "pending" || menu.refund.status === "failed" ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => handleApprove(menu.refund)}
              className="flex w-full items-center px-4 py-2.5 text-left text-sm font-semibold text-[#570033] transition hover:bg-gray-50"
            >
              Refund via Paystack
            </button>
          ) : null}

          {menu.refund.status === "success" ? null : (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => openPrompt(menu.refund, "mark-paid")}
                className="flex w-full items-center px-4 py-2.5 text-left text-sm text-gray-700 transition hover:bg-gray-50"
              >
                Mark as refunded by hand
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => openPrompt(menu.refund, "decline")}
                className="flex w-full items-center px-4 py-2.5 text-left text-sm text-red-600 transition hover:bg-red-50"
              >
                Decline refund
              </button>
            </>
          )}

          {menu.refund.status === "pending" ? null : (
            <button
              type="button"
              role="menuitem"
              onClick={() => openPrompt(menu.refund, "revert")}
              className="flex w-full items-center border-t border-gray-100 px-4 py-2.5 text-left text-sm text-gray-700 transition hover:bg-gray-50"
            >
              Revert status
            </button>
          )}
        </div>
      ) : null}

      {prompt ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">
              {prompt.mode === "decline"
                ? "Decline this refund"
                : prompt.mode === "revert"
                ? "Revert this status"
                : "Record a refund sent by hand"}
            </h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              {prompt.mode === "decline"
                ? `${travellerName(prompt.refund.user)} will be told their ${formatNaira(
                    prompt.refund.amount
                  )} refund was not approved.`
                : prompt.mode === "revert"
                ? "This puts the refund back in the queue as awaiting approval. Money Paystack has already sent cannot be pulled back from here."
                : `Confirm ${formatNaira(prompt.refund.amount)} has been returned to ${travellerName(
                    prompt.refund.user
                  )} outside Paystack.`}
            </p>

            <label className="mt-4 block text-xs font-semibold text-gray-600">
              {prompt.mode === "decline"
                ? "Reason"
                : prompt.mode === "revert"
                ? "What was wrong?"
                : "Note (optional)"}
              <input
                type="text"
                value={promptValue}
                onChange={(e) => setPromptValue(e.target.value)}
                placeholder={
                  prompt.mode === "decline"
                    ? "The trip actually ran"
                    : prompt.mode === "revert"
                    ? "Declined the wrong booking"
                    : "e.g. Bank transfer GTB-9931"
                }
                className="mt-2 w-full rounded-lg border border-[#C9D3E2] px-3 py-2 text-sm text-gray-700 outline-none focus:border-[#570033]"
              />
            </label>

            {actionError ? <p className="mt-3 text-sm text-red-600">{actionError}</p> : null}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closePrompt}
                disabled={isActing}
                className="rounded-full border border-[#C9D3E2] px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-[#570033] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitPrompt}
                disabled={isActing}
                className={`rounded-full px-4 py-2 text-sm font-semibold text-white transition disabled:opacity-50 ${
                  prompt.mode === "decline"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-[#570033] hover:bg-[#450029]"
                }`}
              >
                {isActing
                  ? "Working..."
                  : prompt.mode === "decline"
                  ? "Decline refund"
                  : prompt.mode === "revert"
                  ? "Revert status"
                  : "Mark as refunded"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminLayout>
  );
}

export default AdminRefunds;
